import { useEffect, useState } from "react";

export const DecisionProcessVisual = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const loadTimer = setTimeout(() => setIsLoaded(true), 100);
    
    // Cycle through steps
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);

    return () => {
      clearTimeout(loadTimer);
      clearInterval(stepInterval);
    };
  }, []);

  const steps = [
    { label: "Kontext", sublabel: "erfassen" },
    { label: "Optionen", sublabel: "analysieren" },
    { label: "Trade-offs", sublabel: "abwägen" },
    { label: "Entscheidung", sublabel: "treffen" },
  ];

  return (
    <div className="relative w-full h-full min-h-[400px] flex items-center justify-center">
      {/* Central hub */}
      <div 
        className={`absolute w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center transition-all duration-1000 ${
          isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        }`}
      >
        {/* Pulsing core */}
        <div className="absolute w-8 h-8 rounded-full bg-primary/30 animate-[pulse_3s_ease-in-out_infinite]" />
        <div className="absolute w-4 h-4 rounded-full bg-primary/60" />
        
        {/* Rotating ring */}
        <div 
          className="absolute w-16 h-16 rounded-full border border-dashed border-primary/30 animate-[spin_20s_linear_infinite]"
        />
      </div>

      {/* Orbital paths */}
      <svg 
        className={`absolute w-[320px] h-[320px] transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        viewBox="0 0 320 320"
      >
        {/* Outer orbit */}
        <circle
          cx="160"
          cy="160"
          r="140"
          fill="none"
          stroke="hsl(var(--primary) / 0.1)"
          strokeWidth="1"
          strokeDasharray="8 4"
          className="animate-[spin_60s_linear_infinite_reverse]"
          style={{ transformOrigin: 'center' }}
        />
        
        {/* Inner orbit */}
        <circle
          cx="160"
          cy="160"
          r="100"
          fill="none"
          stroke="hsl(var(--primary) / 0.15)"
          strokeWidth="1"
        />
        
        {/* Connection lines to nodes */}
        {steps.map((_, index) => {
          const angle = (index * 90 - 90) * (Math.PI / 180);
          const x = 160 + 100 * Math.cos(angle);
          const y = 160 + 100 * Math.sin(angle);
          return (
            <line
              key={index}
              x1="160"
              y1="160"
              x2={x}
              y2={y}
              stroke={`hsl(var(--primary) / ${activeStep === index ? 0.4 : 0.1})`}
              strokeWidth={activeStep === index ? 2 : 1}
              className="transition-all duration-500"
            />
          );
        })}
        
        {/* Flow arrow indicator */}
        <g className="animate-[spin_12s_linear_infinite]" style={{ transformOrigin: 'center' }}>
          <circle
            cx="160"
            cy="60"
            r="4"
            fill="hsl(var(--primary) / 0.6)"
          />
        </g>
      </svg>

      {/* Step nodes */}
      {steps.map((step, index) => {
        const angle = (index * 90 - 90) * (Math.PI / 180);
        const radius = 100;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        const isActive = activeStep === index;
        
        return (
          <div
            key={index}
            className={`absolute flex flex-col items-center transition-all duration-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              transform: `translate(${x}px, ${y}px)`,
              transitionDelay: `${index * 150 + 300}ms`,
            }}
          >
            {/* Node */}
            <div 
              className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                isActive 
                  ? 'bg-primary/20 border-primary/40 scale-110 shadow-lg shadow-primary/20' 
                  : 'bg-card/60 border-border/40 scale-100'
              } border backdrop-blur-sm`}
            >
              {/* Step number */}
              <span 
                className={`text-sm font-semibold transition-colors duration-500 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {index + 1}
              </span>
              
              {/* Active indicator */}
              {isActive && (
                <div className="absolute -inset-1 rounded-xl border-2 border-primary/30 animate-[pulse_2s_ease-in-out_infinite]" />
              )}
            </div>
            
            {/* Label */}
            <div 
              className={`mt-2 text-center transition-all duration-500 ${
                isActive ? 'opacity-100' : 'opacity-60'
              }`}
            >
              <p className={`text-xs font-medium transition-colors duration-500 ${
                isActive ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {step.label}
              </p>
              <p className="text-[10px] text-muted-foreground/70">
                {step.sublabel}
              </p>
            </div>
          </div>
        );
      })}

      {/* Decorative particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary/30 animate-[float_8s_ease-in-out_infinite]"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* Bottom text */}
      <div 
        className={`absolute -bottom-4 left-1/2 -translate-x-1/2 text-center transition-all duration-1000 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: '800ms' }}
      >
        <p className="text-xs text-muted-foreground/60">
          Strukturierter Prozess
        </p>
      </div>
    </div>
  );
};
